import React, {
  useState,
  useEffect,
  useRef,
  ReactElement,
  ReactNode,
} from 'react';

import type {
  RenderContentFunction,
  RenderErrorRuleFunction,
  OnChangeFunction,
} from './Types';

interface PasswordWithRulesProps {
  password: string;
  confirmationPassword: string;
  renderContent: RenderContentFunction;
  onChange: OnChangeFunction;
  children: ReactNode;
}

export function PasswordWithRules(props: PasswordWithRulesProps): ReactElement {
  const { password, confirmationPassword, renderContent, children, onChange } =
    props;
  if (!React.isValidElement(children)) {
    throw new Error('Invalid children');
  }
  const rulesContainer = React.cloneElement(children, {
    password,
    confirmationPassword,
    onChange,
  });
  const content = renderContent(rulesContainer);
  return <>{content}</>;
}

interface RulesProps {
  children: ReactNode;
  onChange?: OnChangeFunction;
  password?: string;
  confirmationPassword?: string;
}

export function Rules(props: RulesProps): ReactElement {
  const {
    children,
    password = '',
    confirmationPassword = '',
    onChange,
  } = props;

  const hasAnyRuleError = useRef(false);
  const ruleCounter = useRef(0);
  const totalRules = React.Children.count(children);
  const handleError = (hasError: boolean) => {
    if (hasError) {
      hasAnyRuleError.current = true;
    }
    ruleCounter.current += 1;
  };
  useEffect(() => {
    if (ruleCounter.current === totalRules && onChange) {
      onChange({
        password,
        isValid: !hasAnyRuleError.current,
      });
      hasAnyRuleError.current = false;
      ruleCounter.current = 0;
    }
  }, [totalRules, onChange, password, confirmationPassword, ruleCounter]);
  const newChildren = React.Children.map(children, (error) => {
    if (!React.isValidElement(error)) {
      throw new Error('Invalid error element');
    }
    return React.cloneElement(error, {
      password,
      handleError,
    });
  });
  return <>{newChildren}</>;
}

interface RegexRuleProps {
  handleError?: (hasError: boolean) => void;
  password?: string;
  renderError: RenderErrorRuleFunction;
  regex: RegExp;
}

export function RegexRule(props: RegexRuleProps): ReactElement {
  const { renderError, password = '', regex, handleError } = props;
  const [hasError, setHasError] = useState(true);
  useEffect(() => {
    if (password && password.length > 0) {
      const result = !regex.test(password) || (password.length == 0);
      setHasError(result);
      if (handleError) {
        handleError(result);
      }
    } else{
      setHasError(true);
    }
  }, [password, regex, handleError]);
  return renderError(hasError);
}

interface LengthRuleProps {
  handleError?: (hasError: boolean) => void;
  password?: string;
  renderError: (hasError: boolean, min: number) => JSX.Element;
  min: number;
}

export function LengthRule(props: LengthRuleProps): ReactElement {
  const { renderError, min, password = '', handleError } = props;
  const [hasError, setHasError] = useState(true);
  useEffect(() => {
    if (password && password.length > 0) {
      let result = false;
      if (password.length < min) {
        result = true;
      }
      setHasError(result);
      if (handleError) {
        handleError(result);
      }
    }else {
      setHasError(true);
    }
  }, [password, min, handleError]);
  return renderError(hasError, min);
}

interface BlackListRuleProps {
  handleError?: (hasError: boolean) => void;
  password?: string;
  renderError: RenderErrorRuleFunction;
  list: Array<string>;
}

export function BlackListRule(props: BlackListRuleProps): ReactElement {
  const { renderError, list, password = '', handleError } = props;
  const [hasError, setHasError] = useState(true);
  useEffect(() => {
    if (password && password.length > 0) {
      const passwordMatchInList = list.reduce((match, item) => {
        if (match) return match;
        if (password.indexOf(item) >= 0) {
          return true;
        }
        return false;
      }, false);
      setHasError(passwordMatchInList);
      if (handleError) {
        handleError(passwordMatchInList);
      }
    }
    else {
      setHasError(true);
    }
  }, [password, list, handleError]);
  return renderError(hasError);
}

interface ComparisonValueProps {
  handleError?: (hasError: boolean) => void;
  password?: string;
  renderError: RenderErrorRuleFunction;
  value: string;
}

export function MustMatchRule(props: ComparisonValueProps): ReactElement {
  const { renderError, value, password = '', handleError } = props;
  const [hasError, setHasError] = useState(true);
  useEffect(() => {
    if (password && password.length > 0) {
      const result = password !== value;
      setHasError(result);
      if (handleError) {
        handleError(result);
      }
    }else{
      setHasError(true);
    }
  }, [password, value, handleError]);
  return renderError(hasError);
}
